import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Users,
  ListChecks,
  LayoutDashboard,
  SlidersHorizontal,
  Upload,
} from "lucide-react";
import PhoneSelector from "../../components/PhoneSelector";
import { usePhoneAccount } from "../../contexts/PhoneAccountContext";

import ContactsDashboardPage from "./ContactsDashboardPage";
import ContactsListPage from "./ContactsListPage";
import ContactListsPage from "./ContactListsPage";
import CustomFieldsPage from "./CustomFieldsPage";
import ImportsPage from "./ImportsPage";

/**
 * ContactsHub
 *
 * Top-level container for the centralized contacts module. Tabs are
 * driven by ?tab= so deep links and back/forward work.
 */
const TABS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, Comp: ContactsDashboardPage },
  { id: "contacts", label: "Contacts", icon: Users, Comp: ContactsListPage },
  { id: "lists", label: "Lists", icon: ListChecks, Comp: ContactListsPage },
  { id: "custom-fields", label: "Custom Fields", icon: SlidersHorizontal, Comp: CustomFieldsPage },
  { id: "imports", label: "Imports", icon: Upload, Comp: ImportsPage },
];

const ContactsHub = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedPhoneId } = usePhoneAccount();
  const params = new URLSearchParams(location.search);
  const urlTab = params.get("tab");
  const activeTab = TABS.some((t) => t.id === urlTab) ? urlTab : "dashboard";

  const setActiveTab = (id) => {
    const next = new URLSearchParams(location.search);
    if (next.get("tab") === id) return;
    next.set("tab", id);
    navigate({ pathname: location.pathname, search: next.toString() }, { replace: true });
  };

  useEffect(() => {
    if (!urlTab) setActiveTab("dashboard");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlTab]);

  const ActiveComp = TABS.find((t) => t.id === activeTab)?.Comp;

  return (
    <div>
      <div>
        <div>
          <div className="page-head" style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
            <div>
              <h2 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Contacts</h2>
              <p style={{ color: "var(--text-2)", fontSize: 14, marginTop: 2 }}>
                Manage your centralized contact universe — per WhatsApp phone.
              </p>
            </div>
            <div className="w-full sm:w-auto">
              <PhoneSelector compact section="contacts_management" />
            </div>
          </div>

          <div className="ping-pill-tabs">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const active = tab.id === activeTab;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`ping-ptab ${active ? "on" : ""}`}
                >
                  <Icon size={16} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div>
        {!selectedPhoneId ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10 text-center">
            <h3 className="text-lg font-semibold text-gray-900">Select a WhatsApp phone</h3>
            <p className="text-sm text-gray-500 mt-1">
              Each WhatsApp phone has its own contact universe. Pick one to continue.
            </p>
          </div>
        ) : (
          ActiveComp && <ActiveComp phoneId={selectedPhoneId} setActiveTab={setActiveTab} />
        )}
      </div>
    </div>
  );
};

export default ContactsHub;
